import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// Roles that require phone verification to access the platform
const CREATOR_ROLES = ['artist', 'podcaster', 'streamer', 'studio', 'videographer', 'engineer', 'graphic_designer', 'pr', 'owner'];

// Pages that don't require phone verification
const EXEMPT_PATHS = [
  '/verify-phone',
  '/auth',
  '/auth/callback',
  '/forgot-password',
  '/reset-password',
  '/logout',
  '/',
  '/pricing',
  '/terms',
  '/privacy',
  '/community-guidelines'
];

// TEMPORARILY DISABLED: Phone verification is currently optional
// Twilio trial account cannot send to unverified numbers
// Set this to true when Twilio is properly configured with a paid account
const PHONE_VERIFICATION_REQUIRED = false;

export default function PhoneVerificationWrapper({ children }) {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Phone verification is currently disabled
    if (!PHONE_VERIFICATION_REQUIRED) return;
    
    if (!user || !token) return;
    
    // Check if current path is exempt
    const isExempt = EXEMPT_PATHS.some(path => 
      location.pathname === path || location.pathname.startsWith('/auth')
    );
    if (isExempt) return;

    // Check if user is a creator role
    const isCreator = CREATOR_ROLES.includes(user.role?.toLowerCase());
    
    // If creator and phone not verified, redirect to verification
    if (isCreator && user.needs_phone_verification) {
      toast.info('Please verify your phone number to continue', {
        duration: 5000
      });
      navigate(`/verify-phone?returnTo=${encodeURIComponent(location.pathname)}`);
    }
  }, [user, token, location.pathname, navigate]);

  return children;
}

// Hook for checking if a high-trust feature requires phone verification
export function usePhoneVerificationCheck() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const checkFeatureAccess = (featureName) => {
    if (!user) return false;
    
    // Creators must have verified phone for all features
    const isCreator = CREATOR_ROLES.includes(user.role?.toLowerCase());
    if (isCreator && !user.phone_verified) {
      toast.error('Phone verification required', {
        description: 'Please verify your phone number to use this feature'
      });
      navigate(`/verify-phone?returnTo=${encodeURIComponent(location.pathname)}`);
      return false;
    }
    
    // High-trust features for listeners
    const HIGH_TRUST_FEATURES = [
      'post_create',
      'comment_create', 
      'vote_battle',
      'send_gift',
      'purchase_coins',
      'join_live_chat',
      'submit_report'
    ];
    
    if (user.role === 'listener' && HIGH_TRUST_FEATURES.includes(featureName)) {
      if (!user.phone_verified) {
        toast.error('Phone verification required', {
          description: 'Verify your phone to unlock this feature',
          action: {
            label: 'Verify Now',
            onClick: () => navigate(`/verify-phone?returnTo=${encodeURIComponent(location.pathname)}`)
          }
        });
        return false;
      }
    }
    
    return true;
  };

  return { checkFeatureAccess, isPhoneVerified: user?.phone_verified };
}
